import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ElementRef,
    TemplateRef,
    ApplicationRef,
    ComponentFactoryResolver,
    Injector,
    ViewContainerRef,
    OnChanges,
    SimpleChanges,
} from '@angular/core'
import { Booking } from '@app/domain/Booking'
import { Venue } from '@app/domain/Venue'
import { ProductLibrary } from '@app/domain/ProductLibrary'
import { Menu } from '@app/domain/Consumable'
import { MenuLinkType } from '@app/domain/MenuLinkType'
import { ContextService } from '@app/services/context.service'
import { ProductLibraryService } from '@app/features/management/services/product-library.service'
import { Subject, combineLatest, of } from 'rxjs'
import { catchError, mergeMap, takeUntil } from 'rxjs/operators'
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal'
import { Helper } from '@app/helpers/utilities/helper'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { OrderDetailsModalComponent } from './order-details-modal/order-details-modal.component'
import { OrderDetailsData } from '@app/domain/BookingDocket'

interface PersonSelectionSummary {
    title: string
    index: number
    items: string[]
    notes: string | null
}

@Component({
    selector: 'app-booking-order',
    templateUrl: './booking-order.component.html',
    styleUrls: ['./booking-order.component.sass'],
})
export class BookingOrderComponent implements OnInit, OnChanges, OnDestroy {

    @Input() booking!: Booking
    @Input() venue!: Venue
    @Output() addOrderClicked = new EventEmitter<void>()
    @Output() editOrderClicked = new EventEmitter<void>()
    @Output() removeOrderClicked = new EventEmitter<void>()
    @Output() addMenuClicked = new EventEmitter<void>()
    @Output() editDocketClicked = new EventEmitter<'choices' | 'limits'>()
    @Output() copyMenuLinkClicked = new EventEmitter<MenuLinkType>()
    @Output() resendOrderLinkClicked = new EventEmitter<void>()
    @Output() copyPaymentLinkClicked = new EventEmitter<void>()
    @Output() removeDocketClicked = new EventEmitter<void>()
    @ViewChild('printIframe', { static: false }) printIframe?: ElementRef
    @ViewChild('printContent', { static: false }) printContent?: TemplateRef<any>

    productLibrary: ProductLibrary | null = null
    menu: Menu | null = null
    selectionsByPerson: PersonSelectionSummary[] = []
    loadingMenu = true
    private onDestroy$ = new Subject<void>()
    private portalHost?: DomPortalOutlet
    protected readonly MenuLinkType = MenuLinkType

    constructor(
        private contextService: ContextService,
        private productLibraryService: ProductLibraryService,
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector,
        private viewContainerRef: ViewContainerRef,
        private modalService: NgbModal
    ) {}

    ngOnInit() {
        combineLatest([
            this.contextService.organisation$,
            this.contextService.business$,
            this.contextService.venue$
        ]).pipe(
            takeUntil(this.onDestroy$),
            mergeMap(([organisation, business, venue]) => {
                return this.productLibraryService.getProductLibrary(organisation, business, venue)
                    .pipe(
                        catchError(err => {
                            return of(null)
                        })
                    )
            })
        ).subscribe(pl => {
            this.productLibrary = pl
            this.loadingMenu = false
            this.resolveMenu()
            this.recomputeSelected()
        })
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['booking']) {
            this.resolveMenu()
            this.recomputeSelected()
        }
    }

    ngOnDestroy() {
        this.portalHost?.dispose()
        this.portalHost = undefined
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    onAddOrder() {
        this.addOrderClicked.emit()
    }
    
    onEditOrder() {
        this.editOrderClicked.emit()
    }
    
    onRemoveOrder() {
        this.removeOrderClicked.emit()
    }

    onAddMenu() {
        this.addMenuClicked.emit()
    }

    onEditDocket(mode: 'choices' | 'limits') {
        this.editDocketClicked.emit(mode)
    }

    onRemoveDocket() {
        this.removeDocketClicked.emit()
    }

    onResendOrderLink() {
        this.resendOrderLinkClicked.emit()
    }

    onPrintOrder() {
        setTimeout(() => {
            if (!this.printIframe || !this.printContent) {
                return
            }
            this.attachPrintContentToIframe()
            setTimeout(() => {
                const iframe = this.printIframe!.nativeElement
                iframe.contentWindow.print()
            }, 100)
        }, 0)
    }

    private attachPrintContentToIframe() {
        if (!this.printIframe || !this.printContent) {
            return
        }
        if (this.portalHost) {
            this.portalHost.detach()
        }
        const iframe = this.printIframe.nativeElement
        const body = iframe.contentDocument.body
        while (body.firstChild) {
            body.removeChild(body.firstChild)
        }
        Helper.copyStylesFromDocumentToWindow(document, iframe.contentWindow)
        this.portalHost = new DomPortalOutlet(
            body,
            this.componentFactoryResolver,
            this.appRef,
            this.injector
        )
        const portal = new TemplatePortal(this.printContent, this.viewContainerRef)
        this.portalHost.attach(portal)
    }

    private resolveMenu() {
        if (!this.productLibrary || !this.booking.docket) {
            this.menu = null
            return
        }
        this.menu = this.productLibrary.getMenu(this.booking.docket.menuId)
    }

    private recomputeSelected() {
        const docket = this.booking.docket
        if (!docket || !this.productLibrary) {
            this.selectionsByPerson = []
            return
        }

        const covers = docket.coverChoices || []
        if (!this.menu) {
            this.selectionsByPerson = []
            return
        }
        const orderData = docket.buildOrderDetails(this.menu, this.productLibrary)
        this.selectionsByPerson = orderData.rows
            .map((row, index) => {
                const enteredName = covers[index]?.name?.trim()
                const items = Array.from(row.selectionsByCourse.values()).flat()
                return {
                    title: enteredName && enteredName.length > 0 ? enteredName : `Person ${index + 1}`,
                    index,
                    items,
                    notes: row.notes?.trim() || null
                }
            })
    }

    get hasPersonSelections(): boolean {
        return this.selectionsByPerson.some(person => person.items.length > 0)
    }

    get courseLimitsDisplay(): string | null {
        if (!this.menu) { return null }
        const min = this.booking.docket?.minimumCoursesRequired ?? this.menu.minimumCoursesRequired
        const max = this.booking.docket?.maximumCoursesRequired ?? this.menu.maximumCoursesRequired
        if (min != null && max != null) {
            return min === max ? `${min} ${min === 1 ? 'course' : 'courses'} per guest` : `${min}–${max} courses per guest`
        }
        if (min != null) { return `At least ${min} ${min === 1 ? 'course' : 'courses'} per guest` }
        if (max != null) { return `Up to ${max} ${max === 1 ? 'course' : 'courses'} per guest` }
        return null
    }

    get docketIsComplete(): boolean {
        const docket = this.booking?.docket
        const partySize = this.booking?.size ?? 0
        if (!docket) { return false }
        return docket.isComplete(partySize)
    }

    get docketCompletedCount(): number {
        const docket = this.booking?.docket
        const partySize = this.booking?.size ?? 0
        if (!docket) { return 0 }
        return docket.completedCovers(partySize)
    }

    get docketProgressPercent(): number {
        const docket = this.booking?.docket
        const total = this.booking?.size ?? 0
        if (!docket) { return 0 }
        return docket.progressPercent(total)
    }

    onCopyMenuLink(linkType: MenuLinkType) {
        this.copyMenuLinkClicked.emit(linkType)
    }

    onOpenOrderDetails() {
        const data = this.getOrderDetailsData()
        if (!data) {
            return
        }

        const modalRef = this.modalService.open(OrderDetailsModalComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        })

        const component = modalRef.componentInstance as OrderDetailsModalComponent
        component.data = data
        component.printRequested.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(() => {
            this.onPrintOrder()
        })
    }

    private getOrderDetailsData(): OrderDetailsData | null {
        const docket = this.booking.docket
        if (!docket || !this.productLibrary || !this.menu) {
            return null
        }
        return docket.buildOrderDetails(this.menu, this.productLibrary)
    }
}
